import React, { useCallback, useEffect } from 'react';
import { Modal } from '../design-system';
import styles from './issue-report-popup.module.scss';
import { TextArea } from '../core';
import { EVENTS, Lang } from 'shared/constants';
import { Tag } from 'antd';
import classnames from 'classnames';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import analyticsService from 'src/helpers/analytics.service';
import { useDispatch } from 'src/store/use-dispatch';

const { CheckableTag } = Tag;

const ISSUE_SCHEMA = Yup.object().shape({
  problems: Yup.array().required('Required!'),
  description: Yup.string(),
});

const IssueReportPopupComponent = React.memo(
  ({
    lang = Lang.REPORT_PROBLEM,
    serviceMethod,
    serviceMethodProps,
    open,
    close,
    notificationApi,
    defaultOptions,
  }) => {
    const dispatch = useDispatch();

    // Track the report dialog as a page view when it opens — not on every navigation
    // (this component stays mounted, so a location-driven tracker would misfire app-wide).
    useEffect(() => {
      if (open) {
        analyticsService.track(EVENTS.APPLICATION.PAGE_VIEW, { name: 'Report a problem' });
      }
    }, [open]);
    const openNotification = useCallback(
      ({ message, type = 'success' }) => {
        notificationApi[type]({
          message,
          placement: 'bottomRight',
          duration: 5,
        });
      },
      [notificationApi],
    );

    const submitReport = useCallback(
      async data => {
        const sessionReplyLink = analyticsService.getSessionReplayLink();

        const res = await dispatch(
          serviceMethod({
            data: {
              issue: { ...data, timestamp: new Date().toISOString() },
              device: {
                platform: 'web',
                userAgent: window.navigator.userAgent,
                datadogSessionReplayLink: sessionReplyLink,
              },
              ...serviceMethodProps,
            },
          }),
        );

        if (res?.error) {
          openNotification({ message: lang.NOTIFICATION_ERROR, type: 'error' });
        } else {
          openNotification({ message: lang.NOTIFICATION_SUCCESS });
        }
        close();
      },
      [serviceMethod, serviceMethodProps, dispatch, lang, openNotification, close],
    );

    const { handleSubmit, handleChange, setFieldValue, values, errors, resetForm } = useFormik({
      validationSchema: ISSUE_SCHEMA,
      initialValues: {
        problems: [],
        description: '',
      },
      onSubmit: submitReport,
    });

    const handleTagChange = useCallback(
      (tag, checked) => {
        const nextSelectedTags = checked
          ? [...values.problems, tag]
          : values.problems.filter(t => t !== tag);
        setFieldValue('problems', nextSelectedTags);
      },
      [values.problems, setFieldValue],
    );

    const renderProblemSelector = () =>
      defaultOptions.map(problem => {
        const selected = values.problems.includes(problem);

        return (
          <CheckableTag
            key={problem}
            checked={selected}
            onChange={checked => handleTagChange(problem, checked)}
            tabIndex={0}
            role="checkbox"
            aria-checked={selected}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTagChange(problem, !selected);
              }
            }}
            className={classnames([styles.tag, selected && styles.selected])}>
            {lang?.[problem]}
          </CheckableTag>
        );
      });

    return (
      <Modal
        open={open}
        title={lang.TITLE}
        onCancel={close}
        afterOpenChange={isOpen => {
          if (!isOpen) resetForm();
        }}
        onSubmit={handleSubmit}
        cancelText={lang.DISMISS}
        submitText={lang.SUBMIT}>
        <div className={styles.root}>
          <label className={styles.label}>{lang.PROBLEMS_TITLE}</label>
          <div className={styles.tagWrapper}>{renderProblemSelector()}</div>
          <label className={styles.label}>{lang.DESCRIPTION}</label>
          <TextArea
            wrapperClass={styles.noMargin}
            className={styles.textArea}
            placeholder={lang.DESCRIPTION_PLACEHOLDER}
            name="description"
            value={values.description}
            error={errors.description}
            onChange={handleChange}
          />
        </div>
      </Modal>
    );
  },
);

export { IssueReportPopupComponent };
